/* eslint-disable max-len */
import { useEffect, useState } from "react";
import {
  AutocompleteInput,
  ReferenceInput,
  SimpleForm,
  useCreate,
  useRefresh,
  useTranslate
} from "react-admin";
import { useNavigate } from "react-router-dom";
import { ShowInDialogButton } from "@react-admin/ra-form-layout";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import * as User from "providers/user";
import { CustomButton } from "./CustomButton";
import { muiColors } from "../themes/colors";
import { IconButton } from "@mui/material";
import { CloseIcon } from "../assets/Icons";

const CustomToolbar = ({ loading }: { loading: boolean }) => {
  const translate = useTranslate();

  return (
    <div className="flex justify-end items-center px-[16px] pb-[16px]">
      <CustomButton
        type="submit"
        variant="primary"
        text={translate("ra.action.save")}
        disabled={loading}
      />
    </div>
  );
};

export default function OrderCreateModal({
  isOpen,
  setIsOpen,
  customerUserId,
  locationId
}: {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  customerUserId?: string;
  locationId?: string;
}) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [create] = useCreate();
  const { permissions } = User.useUser();
  const translate = useTranslate();
  const navigate = useNavigate();
  const refresh = useRefresh();

  useEffect(() => {
    if (locationId) {
      submit({ location: { id: locationId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const submit = (data: any) => {
    try {
      setLoading(true);
      const orderPayload = {
        location: data.location,
        full_amount: 0,
        payment_status: "PAY_IN_STORE",
        user: {
          id: customerUserId
        }
      };

      create(
        "Order",
        {
          data: orderPayload
        },
        {
          onSuccess: (order) => {
            setLoading(false);
            order?.id
              ? navigate(`/dashboard/Walk-in-order/${order.id}/show`)
              : navigate("/dashboard/Walk-in-order");
          },
          onError: () => {
            setLoading(false);
          }
        }
      );
    } catch (e) {
      setLoading(false);
      setError(e.message);
    }
  };

  const closeModal = () => {
    setLoading(false);
    setIsOpen(false);
    refresh();
  };

  return (
    <>
      <ShowInDialogButton
        title={translate("order.create_order")}
        label=" "
        fullWidth
        ButtonProps={{
          value: translate("order.finish_order"),
          variant: "contained",
          onClickCapture: () => setIsOpen(true),
          sx: {
            display: "none"
          }
        }}
        isOpen={isOpen}
        onClose={closeModal}
        close={closeModal}
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: muiColors.paper,
            borderRadius: "8px"
          },
          "& #edit-dialog-title": {
            textAlign: "center",
            textTransform: "uppercase",
            fontFamily: "Inter",
            fontSize: "14px"
          },
          "& .RaFormDialogTitle-closeButton": {
            display: "none"
          }
        }}
      >
        <SimpleForm
          resolver={yupResolver(validationSchema)}
          onSubmit={submit}
          toolbar={<CustomToolbar loading={loading} />}
          sx={{
            "& .MuiFilledInput-root": {
              borderRadius: "8px",
              border: "1px solid rgba(48, 47, 46, 0.06)"
            },
            "& .MuiFilledInput-input": {
              height: "24px",
              fontSize: "16px",
              borderRadius: "8px",
              fontFamily: "Inter",
              border: "none"
            },
            "& .MuiFilledInput-input:focus": {
              boxShadow: "none",
              borderRadius: "8px !important"
            },
            "& .MuiFilledInput-root:before": {
              border: "none !important"
            },
            "& .MuiFilledInput-root:after": {
              border: "1px solid #b8722a !important",
              top: "0px",
              borderRadius: "8px",
              transition: "none"
            },
            "& .MuiFilledInput-root.Mui-error:after": {
              border: "1px solid #EF5B1B !important"
            },
            "& .MuiFilledInput-root.Mui-disabled": {
              backgroundColor: "rgba(48, 47, 46, 0.06)"
            },
            "& .MuiTextField-root": {
              marginTop: "0px",
              marginBottom: "0px"
            },
            "& .MuiInputLabel-root": {
              fontSize: "16px",
              color: "rgba(48, 47, 46, 0.6)"
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#b8722a"
            },
            "& .MuiFormHelperText-root": {
              fontSize: "12px",
              marginTop: "2px",
              marginBottom: "2px",
              lineHeight: "14px"
            },
            "[type='number']:focus": {
              boxShadow: "none"
            }
          }}
        >
          {error && <p style={{ color: "red" }}>{error}</p>}
          <ReferenceInput
            source="location.id"
            reference="Location"
            filter={{ company_id: permissions?.companyId }}
          >
            <AutocompleteInput
              optionText={(value) => value.name}
              label={translate("order.locations")}
              fullWidth
            />
          </ReferenceInput>
          <IconButton
            sx={{
              position: "absolute",
              top: "8px",
              right: "8px"
            }}
            onClick={closeModal}
          >
            <CloseIcon />
          </IconButton>
        </SimpleForm>
      </ShowInDialogButton>
    </>
  );
}

const validationSchema = yup
  .object()
  .shape({
    location: yup.object({
      id: yup.string().required("Location selection required.")
    })
  })
  .required();
